import { FC, FocusEvent, ReactNode, useState } from 'react';
import { Button } from 'antd';
import { TbDotsVertical, TbLock } from 'react-icons/tb';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { useNestedResourceAccess } from '@/hooks/useNestedResourceAccess';
import { DragHandle } from '@/shared/components/drag-and-drop';
import { getCollapsibleHoverRevealStyle } from '@/shared/components/layouts/hoverReveal';
import SmallTooltip from '@/shared/components/tooltips/SmallTooltip';

import { useNativeNestedResourceTabDrag, useNestedResourceTabDrag } from '../NestedResourceSortableTabBar/context';

interface NestedResourceTabActionRenderProps {
    onOpenChange: (open: boolean) => void;
    trigger: ReactNode;
}

interface NestedResourceTabLabelProps {
    actionLabel?: string;
    dragHandleLabel?: string;
    icon: ReactNode;
    label: ReactNode;
    locked?: boolean;
    renderActions?: (props: NestedResourceTabActionRenderProps) => ReactNode;
    resourceId?: string;
}

/** Shared label and hover-action treatment for tabs representing child workspace resources. */
const NestedResourceTabLabel: FC<NestedResourceTabLabelProps> = ({
    actionLabel = 'Resource Actions',
    dragHandleLabel = 'Reorder Resource',
    icon,
    label,
    locked = false,
    renderActions,
    resourceId,
}) => {
    const { token } = useAntDesignTheme();
    const providedDragState = useNestedResourceTabDrag();
    const nativeDragState = useNativeNestedResourceTabDrag();
    const resourceAccess = useNestedResourceAccess(nativeDragState?.resource ?? 'reports', resourceId);
    const nativeCanDrag = Boolean(
        nativeDragState &&
        resourceId &&
        nativeDragState.resourceIds.includes(resourceId) &&
        nativeDragState.allowReorder &&
        !nativeDragState.parentLocked &&
        !locked &&
        resourceAccess.canUpdate,
    );
    const dragState =
        providedDragState ??
        (nativeDragState && resourceId
            ? {
                  canDrag: nativeCanDrag,
                  dragHandleProps: null,
                  isDragging: nativeDragState.activeResourceId === resourceId,
              }
            : undefined);
    const [actionsOpen, setActionsOpen] = useState(false);
    const [focused, setFocused] = useState(false);
    const [hovered, setHovered] = useState(false);
    const [leadingFocused, setLeadingFocused] = useState(false);
    const [leadingHovered, setLeadingHovered] = useState(false);
    const visible = hovered || focused || actionsOpen;
    const dragHandleVisible = leadingHovered || leadingFocused || Boolean(dragState?.isDragging);
    const handleBlur = (event: FocusEvent<HTMLSpanElement>) => {
        if (!event.currentTarget.contains(event.relatedTarget)) {
            setFocused(false);
        }
    };
    const trigger = (
        <SmallTooltip padding='0.5rem 0' placement='topRight' title={actionLabel}>
            <Button
                aria-label={actionLabel}
                icon={<TbDotsVertical style={{ color: token.colorTextTertiary }} />}
                size='small'
                type='text'
            />
        </SmallTooltip>
    );

    return (
        <span
            data-nested-resource-tab-label='true'
            onBlur={handleBlur}
            onFocus={() => setFocused(true)}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
            style={{
                alignItems: 'center',
                display: 'inline-flex',
                minWidth: 0,
                opacity: dragState?.isDragging ? 0.55 : 1,
            }}
        >
            <span
                data-nested-resource-tab-leading='true'
                onBlur={(event) => {
                    if (!event.currentTarget.contains(event.relatedTarget)) {
                        setLeadingFocused(false);
                    }
                }}
                onClick={(event) => {
                    if (event.target instanceof Element && event.target.closest('[data-platform-drag-handle]')) {
                        event.preventDefault();
                        event.stopPropagation();
                    }
                }}
                onFocus={() => setLeadingFocused(true)}
                onMouseEnter={() => setLeadingHovered(true)}
                onMouseLeave={() => setLeadingHovered(false)}
                style={{ alignItems: 'center', display: 'inline-flex', flex: 'none', marginInlineEnd: '0.375rem' }}
            >
                {dragState?.canDrag ? (
                    <span
                        data-nested-resource-tab-drag-handle='true'
                        style={getCollapsibleHoverRevealStyle(
                            dragHandleVisible,
                            token,
                            { expandedMaxWidth: '1rem', gap: '0.25rem', side: 'leading' },
                            // Keep the zero-width handle keyboard discoverable. Pointer
                            // access is intentionally activated only from the icon hit-area.
                            { visibility: 'visible' },
                        )}
                    >
                        <DragHandle
                            disabled={!dragState.canDrag}
                            dragHandleProps={dragState.dragHandleProps}
                            isDragging={dragState.isDragging}
                            label={dragHandleLabel}
                            nativeDraggable={Boolean(nativeDragState && resourceId)}
                            onNativeDragEnd={() => nativeDragState?.cancelDrag()}
                            onNativeDragStart={(event) => {
                                if (!nativeCanDrag || !nativeDragState || !resourceId) {
                                    event.preventDefault();
                                    return;
                                }

                                event.stopPropagation();
                                event.dataTransfer.effectAllowed = 'move';
                                event.dataTransfer.setData('text/plain', resourceId);
                                nativeDragState.startDrag(resourceId, event);
                            }}
                            style={{ height: '1rem', minWidth: '1rem' }}
                        />
                    </span>
                ) : null}
                <span aria-hidden style={{ alignItems: 'center', display: 'inline-flex' }}>
                    {icon}
                </span>
            </span>
            <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {label}
            </span>
            {locked ? (
                <SmallTooltip padding='0.5rem 0' placement='top' title='Locked'>
                    <span
                        aria-label='Locked'
                        style={{
                            alignItems: 'center',
                            display: 'inline-flex',
                            flex: 'none',
                            marginInlineStart: '0.375rem',
                        }}
                    >
                        <TbLock size='0.75rem' style={{ color: token.colorTextTertiary }} />
                    </span>
                </SmallTooltip>
            ) : null}
            {renderActions ? (
                <span data-nested-resource-tab-actions='true' style={getCollapsibleHoverRevealStyle(visible, token)}>
                    {renderActions({ onOpenChange: setActionsOpen, trigger })}
                </span>
            ) : null}
        </span>
    );
};

export default NestedResourceTabLabel;
export type { NestedResourceTabActionRenderProps, NestedResourceTabLabelProps };
